import { inject } from '@angular/core';
import { Routes } from '@angular/router';
import { AuthGuard } from './auth/guards/auth.guard';
import { BlockRootUserGuard } from './auth/guards/block-root-user.guard';
import { HasAdminRoleGuard } from './auth/guards/has-admin-role.guard';
import { HasRootRoleGuard } from './auth/guards/has-root-role.guard';
import { HasUserRoleGuard } from './auth/guards/has-user-role.guard';
import { LogoutGuard } from './auth/guards/logout.guard';
import { RedirectIfAuthenticatedGuard } from './auth/guards/redirect-if-authenticated.guard';
import { RedirectUnknownRoleGuard } from './auth/guards/redirect-unknown-role.guard';
import { TermsAcceptanceGuard } from './auth/guards/terms-acceptance.guard';
import { BLOG_ROUTES } from './blog/blog-routes.generated';
import { DataService } from './data/services/data.service';
import { DOCS_ROUTES } from './docs/docs-routes.generated';
import { privateAppGuard } from '@core/guards/private-app.guard';
import { staticHtmlResolver } from '@core/resolvers/static-html.resolver';

const dataRefreshResolver = () => {
  const dataService = inject(DataService);
  dataService.refresh();
  return;
};

const publicRoutes: Routes = [
  {
    path: '',
    loadComponent: () => import('./public/components/home/home.component').then(m => m.HomeComponent),
  },
  {
    path: 'status',
    loadComponent: () => import('./status/components/status.component').then(m => m.StatusComponent),
  },
  {
    path: 'pricing',
    loadComponent: () => import('./public/components/pricing/pricing.component').then(m => m.PricingComponent),
  },
  {
    path: 'new-service',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./srvs/components/new-service-redirect/new-service-redirect.component').then(
        m => m.NewServiceRedirectComponent
      ),
  },
  // The campaign landing page became an article; the URL stays for the links out there.
  {
    path: 'mongo-atlas-data-api-replacement',
    loadComponent: () => import('./core/components/go-to/go-to.component').then(m => m.GoToComponent),
    data: { to: '/blog/mongodb-atlas-data-api-replacement' },
  },
  {
    path: 'mongo-data-api-replacement',
    redirectTo: 'mongo-atlas-data-api-replacement',
  },
  // Retired: it said, less well, what the home page and /features already say. Its one
  // distinctive part — where we are the wrong answer — moved to /features.
  {
    path: 'why-restheart-cloud',
    redirectTo: 'features',
    pathMatch: 'full',
  },
  // Retired: a page about another product. The URL stays for the links out there.
  {
    path: 'sophia',
    redirectTo: 'features',
    pathMatch: 'full',
  },
  {
    path: 'vibe-coding',
    loadComponent: () =>
      import('./public/components/vibe-coding/vibe-coding.component').then(m => m.VibeCodingComponent),
  },
  {
    path: 'starters',
    loadComponent: () => import('./public/components/starters/starters.component').then(m => m.StartersComponent),
  },
  {
    path: 'features',
    loadComponent: () => import('./public/components/features/features.component').then(m => m.FeaturesComponent),
  },
  // The page used to be called Plugins, which named how the server is built rather than what
  // a reader gets. Links and search results still point here.
  {
    path: 'plugins',
    redirectTo: 'features',
    pathMatch: 'full',
  },
  {
    path: 'blog',
    children: [
      // the posts that carried the old name in their address: the old links still land
      { path: 'what-is-restheart-cloud', redirectTo: 'what-is-ulabase', pathMatch: 'full' },
      { path: 'restheart-cloud-kit-starter-apps', redirectTo: 'ulabase-kit-starter-apps', pathMatch: 'full' },
      { path: 'vibe-coding-with-restheart-cloud', redirectTo: 'vibe-coding-with-ulabase', pathMatch: 'full' },
      { path: 'restheart-cloud-june-2026-updates', redirectTo: 'ulabase-june-2026-updates', pathMatch: 'full' },
      ...BLOG_ROUTES,
    ],
  },
  {
    path: 'docs',
    children: [
      // the page was replaced by the starters; old links, blog posts and tip emails still point at it
      { path: 'full-stack-example', redirectTo: 'starters', pathMatch: 'full' },
      ...DOCS_ROUTES,
    ],
  },
  {
    path: 'signin',
    redirectTo: 'auth/login',
    pathMatch: 'full',
  },
  {
    path: 'auth/login',
    canActivate: [RedirectIfAuthenticatedGuard],
    loadComponent: () => import('./auth/components/signin/signin.component').then(m => m.SigninComponent),
  },
  {
    path: 'auth/forgot-pwd',
    loadComponent: () =>
      import('./auth/components/pwd-reset-request/pwd-reset-request').then(m => m.PwdResetRequestComponent),
  },
  {
    path: 'auth/check-your-email',
    loadComponent: () =>
      import('./auth/components/check-your-email/check-your-email.component').then(m => m.CheckYourEmailComponent),
  },
  {
    path: 'oauth',
    children: [
      {
        path: 'success',
        loadComponent: () =>
          import('./auth/components/oauth-success/oauth-success.component').then(m => m.OAuthSuccessComponent),
      },
      {
        path: 'error',
        loadComponent: () =>
          import('./auth/components/oauth-error/oauth-error.component').then(m => m.OAuthErrorComponent),
      },
    ],
  },
  {
    path: 'auth/verify',
    loadComponent: () =>
      import('./auth/components/verify-email/verify-email.component').then(m => m.VerifyEmailComponent),
  },
  {
    path: 'auth/activate',
    canActivate: [LogoutGuard],
    loadComponent: () =>
      import('./auth/components/activate-account/activate-account.component').then(m => m.ActivateAccountComponent),
  },
  {
    path: 'auth/reset-password',
    canActivate: [LogoutGuard],
    loadComponent: () =>
      import('./auth/components/activate-account/activate-account.component').then(m => m.ActivateAccountComponent),
  },
  {
    path: 'auth/oauth/:provider/callback',
    loadComponent: () =>
      import('./auth/components/oauth-success/oauth-success.component').then(m => m.OAuthSuccessComponent),
  },
  {
    path: 'accept-terms',
    canActivate: [AuthGuard],
    loadComponent: () =>
      import('./auth/components/accept-terms/accept-terms.component').then(m => m.AcceptTermsComponent),
  },
  {
    path: 'signup',
    redirectTo: 'auth/signup',
    pathMatch: 'full',
  },
  {
    path: 'auth/signup',
    canActivate: [LogoutGuard],
    loadComponent: () => import('./auth/components/singup/signup.component').then(m => m.SignupComponent),
  },
  {
    path: 'invitations/accept',
    loadComponent: () =>
      import('./orgs/components/accept-invite/accept-invite.component').then(m => m.AcceptInviteComponent),
  },
  {
    path: 'terms',
    loadComponent: () =>
      import('./public/components/legal-page/legal-page.component').then(m => m.LegalPageComponent),
    resolve: { htmlContent: staticHtmlResolver('legal') },
    data: {
      htmlFile: 'terms.html',
      path: 'terms',
      title: 'Ulabase Terms and Conditions',
      description: 'The general conditions of the Ulabase service provided by SoftInstigate Srl Società Benefit.',
    },
  },
  {
    path: 'privacy',
    loadComponent: () =>
      import('./public/components/legal-page/legal-page.component').then(m => m.LegalPageComponent),
    resolve: { htmlContent: staticHtmlResolver('legal') },
    data: {
      htmlFile: 'privacy.html',
      path: 'privacy',
      title: 'Ulabase Privacy Policy',
      description: 'How SoftInstigate Srl Società Benefit processes personal data for the Ulabase service.',
    },
  },
  {
    path: 'cookies',
    loadComponent: () =>
      import('./public/components/legal-page/legal-page.component').then(m => m.LegalPageComponent),
    resolve: { htmlContent: staticHtmlResolver('legal') },
    data: {
      htmlFile: 'cookies.html',
      path: 'cookies',
      title: 'Ulabase Cookie Policy',
      description: 'The cookies that Ulabase uses, all of them technical.',
    },
  },
];

const protectedRoutes: Routes = [
  // Legacy routes - redirect to new structure
  {
    path: 'srvs',
    canActivate: [HasUserRoleGuard],
    children: [
      {
        path: '',
        redirectTo: '/orgs',
        pathMatch: 'full',
      },
      {
        path: ':srvid',
        redirectTo: '/orgs',
        pathMatch: 'full',
      },
    ],
  },
  // New org-scoped routes
  {
    path: 'orgs',
    canActivate: [HasUserRoleGuard],
    resolve: { refresh: dataRefreshResolver },
    children: [
      {
        path: '',
        loadComponent: () => import('./orgs/components/orgs-list/orgs-list.component').then(m => m.OrgsListComponent),
      },
      {
        path: 'new',
        loadComponent: () =>
          import('./orgs/components/create-org-page/create-org-page.component').then(m => m.CreateOrgPageComponent),
      },
      {
        path: 'active',
        loadComponent: () =>
          import('./orgs/components/active-org-redirect/active-org-redirect.component').then(
            m => m.ActiveOrgRedirectComponent
          ),
        children: [
          {
            path: '**',
            loadComponent: () =>
              import('./orgs/components/active-org-redirect/active-org-redirect.component').then(
                m => m.ActiveOrgRedirectComponent
              ),
          },
        ],
      },
      {
        path: ':orgId',
        loadComponent: () =>
          import('./orgs/components/org-layout/org-layout.component').then(m => m.OrgLayoutComponent),
        children: [
          {
            path: '',
            loadComponent: () =>
              import('./orgs/components/active-org/active-org.component').then(m => m.ActiveOrgComponent),
          },
          // Organization management with sidebar (overview, members, billing)
          {
            path: 'overview',
            redirectTo: '',
            pathMatch: 'full',
          },
          {
            path: 'team',
            loadComponent: () =>
              import('./orgs/components/org-members/org-members.component').then(m => m.OrgMembersComponent),
          },
          {
            path: 'billing',
            loadComponent: () =>
              import('./orgs/components/billing-placeholder/billing-placeholder.component').then(
                m => m.BillingPlaceholderComponent
              ),
          },
          // the way in to support: the services, each with its support page
          {
            path: 'support',
            loadComponent: () =>
              import('./orgs/components/org-support/org-support.component').then(m => m.OrgSupportComponent),
          },
          {
            path: 'settings',
            loadComponent: () =>
              import('./orgs/components/org-settings/org-settings.component').then(m => m.OrgSettingsComponent),
          },
          // Service creation
          {
            path: 'new-service',
            loadComponent: () =>
              import('./srvs/components/service-creation-wizard/service-creation-wizard.component').then(
                m => m.ServiceCreationWizardComponent
              ),
          },
          // Services list
          {
            path: 'srvs',
            children: [
              {
                path: '',
                resolve: { refresh: dataRefreshResolver },
                loadComponent: () =>
                  import('./srvs/components/srvs-list/srvs-list.component').then(m => m.SrvsListComponent),
              },
              // Service pages - no wrapper, rendered directly in org-layout
              {
                path: ':serviceId',
                children: [
                  {
                    path: '',
                    loadComponent: () =>
                      import('./srvs/components/srv-overview-page/srv-overview-page.component').then(
                        m => m.SrvOverviewPageComponent
                      ),
                  },
                  {
                    path: 'monitor',
                    loadComponent: () =>
                      import('./srvs/components/srv-monitor-page/srv-monitor-page.component').then(
                        m => m.SrvMonitorPageComponent
                      ),
                  },
                  {
                    // Connect is a section of Home now; old links and bookmarks land there
                    path: 'connect',
                    redirectTo: '',
                    pathMatch: 'full',
                  },
                  // where a new service lands: that it is ready, its address, root's password and the next
                  // steps; not in the side menu (specs/todo/servizio-pronto.md)
                  {
                    path: 'ready',
                    loadComponent: () =>
                      import('./srvs/components/ready-page/ready-page.component').then(m => m.ReadyPageComponent),
                  },
                  // the starter apps, from the side menu
                  {
                    path: 'start',
                    loadComponent: () =>
                      import('./srvs/components/start-page/start-page.component').then(m => m.StartPageComponent),
                  },
                  {
                    path: 'api-tutorial',
                    loadComponent: () =>
                      import('./srvs/components/api-tutorial-page/api-tutorial-page.component').then(
                        m => m.ApiTutorialPageComponent
                      ),
                  },
                  // specs/todo/ai-providers.md
                  {
                    path: 'ai-providers',
                    loadComponent: () =>
                      import('./srvs/components/ai-providers-page/ai-providers-page.component').then(
                        m => m.AiProvidersPageComponent
                      ),
                  },
                  // specs/todo/ai-console-presentation.md
                  {
                    path: 'auto-embeddings',
                    loadComponent: () =>
                      import('./srvs/components/auto-embeddings-page/auto-embeddings-page.component').then(
                        m => m.AutoEmbeddingsPageComponent
                      ),
                  },
                  // specs/todo/ai-file-bucket-chunking.md
                  {
                    path: 'auto-chunking',
                    loadComponent: () =>
                      import('./srvs/components/auto-chunking-page/auto-chunking-page.component').then(
                        m => m.AutoChunkingPageComponent
                      ),
                  },
                  // the placeholder's route, before the page shipped
                  { path: 'chunking', redirectTo: 'auto-chunking', pathMatch: 'full' },
                  {
                    path: 'mcp',
                    loadComponent: () =>
                      import('./srvs/components/mcp-page/mcp-page.component').then(m => m.McpPageComponent),
                  },
                  {
                    path: 'features',
                    loadComponent: () =>
                      import('./srvs/components/features-page/features-page.component').then(m => m.FeaturesPageComponent),
                  },
                  {
                    path: 'api-keys',
                    loadComponent: () =>
                      import('./srvs/components/api-keys-page/api-keys-page.component').then(
                        m => m.ApiKeysPageComponent
                      ),
                  },
                  {
                    path: 'llm',
                    loadComponent: () =>
                      import('./srvs/components/llm-plugin-page/llm-plugin-page.component').then(
                        m => m.LlmPluginPageComponent
                      ),
                  },
                  {
                    path: 'databases',
                    loadComponent: () =>
                      import('./srvs/components/databases-page/databases-page.component').then(
                        m => m.DatabasesPageComponent
                      ),
                  },
                  {
                    path: 'collections',
                    loadComponent: () =>
                      import('./srvs/components/collections-browser-page/collections-browser-page.component').then(
                        m => m.CollectionsBrowserPageComponent
                      ),
                  },
                  {
                    path: 'collections/:collectionId',
                    loadComponent: () =>
                      import('./srvs/components/documents-page/documents-page.component').then(
                        m => m.DocumentsPageComponent
                      ),
                  },
                  {
                    path: 'mails',
                    loadComponent: () =>
                      import('./srvs/components/mails-plugin-page/mails-plugin-page.component').then(
                        m => m.MailsPluginPageComponent
                      ),
                  },
                  {
                    path: 'webhooks',
                    loadComponent: () =>
                      import('./srvs/components/webhooks-plugin-page/webhooks-plugin-page.component').then(
                        m => m.WebhooksPluginPageComponent
                      ),
                  },
                  {
                    path: 'users',
                    loadComponent: () =>
                      import('./srvs/components/users-management-page/users-management-page.component').then(
                        m => m.UsersManagementPageComponent
                      ),
                  },
                  {
                    path: 'acl',
                    loadComponent: () =>
                      import('./srvs/components/acl-management-page/acl-management-page.component').then(
                        m => m.AclManagementPageComponent
                      ),
                  },
                  {
                    path: 'schemas',
                    loadComponent: () =>
                      import('./srvs/components/schemas-page/schemas-page.component').then(m => m.SchemasPageComponent),
                  },
                  {
                    path: 'graphql',
                    loadComponent: () =>
                      import('./srvs/components/graphql-page/graphql-page.component').then(m => m.GraphqlPageComponent),
                  },
                  {
                    path: 'aggregations',
                    loadComponent: () =>
                      import('./srvs/components/aggregations-page/aggregations-page.component').then(
                        m => m.AggregationsPageComponent
                      ),
                  },
                  {
                    path: 'constraints',
                    loadComponent: () =>
                      import('./srvs/components/constraints-page/constraints-page.component').then(
                        m => m.ConstraintsPageComponent
                      ),
                  },
                  {
                    path: 'indexes',
                    loadComponent: () =>
                      import('./srvs/components/indexes-page/indexes-page.component').then(m => m.IndexesPageComponent),
                  },
                  {
                    path: 'websocket',
                    loadComponent: () =>
                      import('./srvs/components/websocket-page/websocket-page.component').then(
                        m => m.WebsocketPageComponent
                      ),
                  },
                  {
                    path: 'signup',
                    loadComponent: () =>
                      import('./srvs/components/srv-signup-page/srv-signup-page.component').then(
                        m => m.SrvSignupPageComponent
                      ),
                  },
                  {
                    path: 'origin-allowlist',
                    loadComponent: () =>
                      import('./srvs/components/origin-allowlist-page/origin-allowlist-page.component').then(
                        m => m.OriginAllowlistPageComponent
                      ),
                  },
                  {
                    path: 'guards',
                    loadComponent: () =>
                      import('./srvs/components/guards-page/guards-page.component').then(m => m.GuardsPageComponent),
                  },
                  {
                    path: 'stripe',
                    loadComponent: () =>
                      import('./srvs/components/stripe-page/stripe-page.component').then(m => m.StripePageComponent),
                  },
                  {
                    path: 'support',
                    loadComponent: () =>
                      import('./srvs/components/srv-support-page/srv-support-page.component').then(
                        m => m.SrvSupportPageComponent
                      ),
                  },
                  // specs/todo/branding.md
                  {
                    path: 'branding',
                    loadComponent: () =>
                      import('./srvs/components/branding-page/branding-page.component').then(m => m.BrandingPageComponent),
                  },
                  {
                    path: 'settings',
                    loadComponent: () =>
                      import('./srvs/components/srv-settings-page/srv-settings-page.component').then(
                        m => m.SrvSettingsPageComponent
                      ),
                  },
                ],
              },
            ],
          },
          {
            path: 'admin/users',
            canActivate: [HasAdminRoleGuard],
            loadChildren: () => import('./admin/management/management.module').then(m => m.ManagementModule),
          },
        ],
      },
    ],
  },
  // Legacy org and billing routes - redirect to new structure
  {
    path: 'org',
    redirectTo: '/orgs',
    pathMatch: 'full',
  },
  {
    path: 'billing',
    redirectTo: '/orgs',
    pathMatch: 'full',
  },
  {
    path: 'mgmt',
    redirectTo: '/orgs',
    pathMatch: 'full',
  },

  {
    path: 'get/free',
    redirectTo: '/orgs/active/new-service?tier=free',
    pathMatch: 'full',
  },
  {
    path: 'get/shared',
    redirectTo: '/orgs/active/new-service?tier=shared',
    pathMatch: 'full',
  },
  {
    path: 'assistant',
    loadComponent: () => import('./support/components/get-help/get-help.component').then(m => m.GetHelpComponent),
  },
];

export const routes: Routes = [
  {
    path: '',
    loadComponent: () => import('./core/components/layout/layout.component').then(m => m.LayoutComponent),
    // a no-op unless environment.PRIVATE_APP_URL says the console runs elsewhere
    canActivateChild: [privateAppGuard],
    children: [
      // Root user routes
      {
        path: 'analytics',
        canActivate: [AuthGuard, HasRootRoleGuard],
        loadComponent: () =>
          import('./root-admin/components/user-statistics/user-statistics.component').then(
            m => m.UserStatisticsComponent
          ),
      },
      {
        path: 'navigation-analytics',
        canActivate: [AuthGuard, HasRootRoleGuard],
        loadComponent: () =>
          import('./root-admin/components/navigation-analytics/navigation-analytics.component').then(
            m => m.NavigationAnalyticsComponent
          ),
      },
      {
        path: 'api-usage-analytics',
        canActivate: [AuthGuard, HasRootRoleGuard],
        loadComponent: () =>
          import('./root-admin/components/api-usage-analytics/api-usage-analytics.component').then(
            m => m.ApiUsageAnalyticsComponent
          ),
      },
      {
        path: 'support-chats',
        canActivate: [AuthGuard, HasRootRoleGuard],
        loadComponent: () =>
          import('./root-admin/components/support-chats/support-chats.component').then(m => m.SupportChatsComponent),
      },
      {
        path: 'emails',
        canActivate: [AuthGuard, HasRootRoleGuard],
        loadComponent: () =>
          import('./root-admin/components/email-preview/email-preview.component').then(m => m.EmailPreviewComponent),
      },
      ...publicRoutes,
      // /me route accessible by all authenticated users (including root)
      {
        path: 'me',
        canActivate: [AuthGuard, TermsAcceptanceGuard],
        resolve: { refresh: dataRefreshResolver },
        loadComponent: () =>
          import('./core/components/user-profile/user-profile.component').then(m => m.UserProfileComponent),
      },
      {
        // Next to /me rather than under an org: a token belongs to the person,
        // not to a team, and keeps working whichever org is selected.
        path: 'me/tokens',
        canActivate: [AuthGuard, TermsAcceptanceGuard],
        loadComponent: () =>
          import('./core/components/access-tokens/access-tokens.component').then(m => m.AccessTokensComponent),
      },
      {
        path: '',
        canActivate: [AuthGuard, TermsAcceptanceGuard, BlockRootUserGuard, RedirectUnknownRoleGuard],
        children: protectedRoutes,
      },
      // 404 page inside layout to get navigation
      {
        path: 'not-found',
        loadComponent: () =>
          import('./core/components/page-not-found/page-not-found.component').then(m => m.PageNotFoundComponent),
      },
    ],
  },
  {
    path: 'check-your-email',
    canActivate: [privateAppGuard],
    loadComponent: () =>
      import('./auth/components/check-your-email/check-your-email.component').then(m => m.CheckYourEmailComponent),
  },
  {
    path: 'unsubscribe-tips',
    canActivate: [privateAppGuard],
    loadComponent: () =>
      import('./core/components/unsubscribe-tips/unsubscribe-tips.component').then(m => m.UnsubscribeTipsComponent),
  },
  {
    path: 'app',
    redirectTo: '/',
    pathMatch: 'prefix',
  },
  {
    path: '**',
    redirectTo: '/not-found',
  },
];
